import { useState, useEffect, useCallback, useRef } from 'react';

interface QueuedAction {
  id: string;
  action: () => Promise<unknown>;
  timestamp: number;
}

interface UseOnlineStatusReturn {
  isOnline: boolean;
  wasOffline: boolean;
  queueAction: (action: () => Promise<unknown>) => string;
  cancelAction: (id: string) => void;
  pendingActionsCount: number;
}

export function useOnlineStatus(): UseOnlineStatusReturn {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const queuedActionsRef = useRef<QueuedAction[]>([]);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  // Generate unique ID for actions
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Process queued actions when back online
  const processQueue = useCallback(async () => {
    const actions = [...queuedActionsRef.current];
    if (actions.length === 0) return;

    console.log(`[Offline Queue] Processing ${actions.length} queued actions`);

    for (const queuedAction of actions) {
      try {
        await queuedAction.action();
        // Remove successful action from queue
        queuedActionsRef.current = queuedActionsRef.current.filter(
          (a) => a.id !== queuedAction.id
        );
        setPendingActionsCount(queuedActionsRef.current.length);
      } catch (error) {
        console.error('[Offline Queue] Failed to process action:', error);
        // Keep failed action in queue for retry
      }
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Network] Back online');
      setIsOnline(true);
      // Process queued actions
      processQueue();
    };

    const handleOffline = () => {
      console.log('[Network] Went offline');
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processQueue]);

  // Reset wasOffline flag after a delay
  useEffect(() => {
    if (wasOffline && isOnline) {
      const timer = setTimeout(() => {
        setWasOffline(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [wasOffline, isOnline]);

  // Queue an action to be executed when online
  const queueAction = useCallback(
    (action: () => Promise<unknown>): string => {
      const id = generateId();
      queuedActionsRef.current.push({
        id,
        action,
        timestamp: Date.now(),
      });
      setPendingActionsCount(queuedActionsRef.current.length);
      console.log('[Offline Queue] Action queued:', id);
      return id;
    },
    [generateId]
  );

  // Cancel a queued action
  const cancelAction = useCallback((id: string) => {
    queuedActionsRef.current = queuedActionsRef.current.filter((a) => a.id !== id);
    setPendingActionsCount(queuedActionsRef.current.length);
    console.log('[Offline Queue] Action cancelled:', id);
  }, []);

  return {
    isOnline,
    wasOffline,
    queueAction,
    cancelAction,
    pendingActionsCount,
  };
}
